import { z } from "zod";
import { sessionDocumentSchema } from "@event-planner/shared";

/**
 * Admin form schema for the `sessions` collection.
 *
 * Per ADR-083, the canonical document shape lives at
 * `packages/shared/src/schemas/session.ts`. This file picks the
 * form-editable subset and layers form-specific validation on top.
 *
 * Server-managed and form-irrelevant fields are omitted:
 * - `eventId` — set by route param, not edited in the form
 * - `date`, `targetedPersonIds`, `speakerNames`, `speakerIds`,
 *   `locationLabel` — CF-managed
 * - `startTime` / `endTime` — captured as datetime-local strings in the
 *   form and converted to TimestampLike at the service boundary; we
 *   redefine them as strings here.
 * - `audienceId` — composed by the service from the form's
 *   `pickedAudienceIds` (the audience picker output) using the
 *   audience writeBatch flow per ADR-120.
 *
 * `pickedAudienceIds[]` is the form's audience knob — the IDs the
 * picker resolved to (named audiences directly, plus personal-audience
 * IDs for picked emails). The service either reuses a single audience
 * or composes an anonymous audience around them.
 */
const sessionDocumentFormBase = sessionDocumentSchema.omit({
  eventId: true,
  date: true,
  targetedPersonIds: true,
  speakerNames: true,
  speakerIds: true,
  startTime: true,
  endTime: true,
  locationLabel: true,
  audienceId: true,
});

export const sessionSchema = z
  .object({
    ...sessionDocumentFormBase.shape,
    title: z.string().min(1, "Title is required"),
    sessionTypeId: z.string().min(1, "Category is required"),
    pickedAudienceIds: z.array(z.string()).min(1, "Pick at least one audience"),
    startTime: z.string().min(1, "Start time is required"),
    endTime: z.string().min(1, "End time is required"),
  })
  .refine((data) => data.endTime > data.startTime, {
    message: "End time must be after start time",
    path: ["endTime"],
  });

export type SessionFormData = z.infer<typeof sessionSchema>;
